import { useState } from "react";
import axios from "axios";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, ChevronDown } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm({
  variant = "light",
  glass = false,
  submitLabel = "Submit & get a call back",
  testidPrefix = "lead",
  defaultCourse = "",
}) {
  const { content } = useContent();
  const courses = (content?.courses?.items || []).map((c) => c.title || c.short).filter(Boolean);

  const [form, setForm] = useState({ full_name: "", mobile: "", course: defaultCourse, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.trim().length < 2) return toast.error("Please enter your full name.");
    if (!/^[0-9+\-\s()]{7,20}$/.test(form.mobile.trim())) return toast.error("Please enter a valid mobile number.");
    if (courses.length > 0 && !form.course.trim()) return toast.error("Please select a course.");
    if (!form.message.trim()) return toast.error("Please add a short message or query.");

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        full_name: form.full_name,
        mobile: form.mobile,
        message: form.message,
        course: form.course || null,
      });
      setDone(true);
      toast.success("Thank you! We'll call you back shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ full_name: "", mobile: "", course: defaultCourse, message: "" });
  };

  const wrapperCls = variant === "card"
    ? "h-full p-7 lg:p-9 rounded-2xl bg-white border border-border shadow-sm"
    : "";

  const labelCls = glass
    ? "text-[10px] uppercase tracking-widest text-white/85 font-semibold"
    : "text-xs uppercase tracking-widest text-muted-foreground";
  const inputCls = glass
    ? "mt-1 h-10 text-white placeholder:text-white/70 focus-visible:ring-white/40"
    : "mt-1 h-10";
  const textareaCls = glass
    ? "text-white placeholder:text-white/70 focus-visible:ring-white/40 mt-1"
    : "mt-1";
  const inputStyle = glass
    ? { backgroundColor: "rgba(255,255,255,0.10)", borderColor: "rgba(255,255,255,0.35)" }
    : undefined;
  const selectCls = glass
    ? "mt-1 h-10 w-full rounded-md border px-3 pr-9 text-sm appearance-none text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 [color-scheme:dark]"
    : "mt-1 h-10 w-full rounded-md border border-input bg-white px-3 pr-9 text-sm appearance-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";
  const disclaimerCls = glass
    ? "text-[10px] text-white/70 text-center leading-relaxed"
    : "text-[11px] text-muted-foreground text-center leading-relaxed";

  if (done) {
    return (
      <div className={wrapperCls + " flex flex-col items-center justify-center text-center"}>
        <CheckCircle2 className={`h-12 w-12 ${glass ? "text-white" : "text-primary"}`} />
        <h3 className={`font-display mt-5 text-xl lg:text-2xl font-medium ${glass ? "text-white" : ""}`}>You&apos;re on the list!</h3>
        <p className={`mt-3 max-w-sm text-sm leading-relaxed ${glass ? "text-white/80" : "text-muted-foreground"}`}>
          Thanks, {form.full_name.split(" ")[0]}. Our admissions counsellor will reach out shortly.
        </p>
        <button
          type="button"
          onClick={reset}
          data-testid={`${testidPrefix}-reset`}
          className={`mt-6 text-sm font-medium hover:underline ${glass ? "text-white" : "text-primary"}`}
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={wrapperCls + " flex flex-col"}
      data-testid={`${testidPrefix}-form`}
    >
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Full name</label>
          <Input
            data-testid={`${testidPrefix}-input-name`}
            value={form.full_name}
            onChange={handle("full_name")}
            placeholder="e.g. Riya Sharma"
            className={inputCls}
            style={inputStyle}
            required
          />
        </div>
        <div>
          <label className={labelCls}>Mobile number</label>
          <Input
            data-testid={`${testidPrefix}-input-mobile`}
            value={form.mobile}
            onChange={handle("mobile")}
            placeholder="+91 98xxx xxxxx"
            inputMode="tel"
            className={inputCls}
            style={inputStyle}
            required
          />
        </div>
        {courses.length > 0 && (
          <div>
            <label className={labelCls}>Course of interest</label>
            <div className="relative">
              <select
                data-testid={`${testidPrefix}-input-course`}
                value={form.course}
                onChange={handle("course")}
                className={selectCls}
                style={inputStyle}
                required
              >
                <option value="" disabled>Select a course</option>
                {courses.map((c) => (
                  <option key={c} value={c} className="text-foreground">{c}</option>
                ))}
              </select>
              <ChevronDown className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 ${glass ? "text-white/70" : "text-muted-foreground"}`} />
            </div>
          </div>
        )}
        <div>
          <label className={labelCls}>Your message</label>
          <Textarea
            data-testid={`${testidPrefix}-input-message`}
            value={form.message}
            onChange={handle("message")}
            placeholder="Any specific query? Batch timing, fees, etc."
            rows={2}
            className={textareaCls}
            style={inputStyle}
            required
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid={`${testidPrefix}-submit`}
          className={`w-full h-11 ${glass ? "bg-white text-[#0b1442] hover:bg-white/90" : "bg-primary text-white hover:bg-[#059669]"} disabled:opacity-70`}
        >
          {loading ? "Sending..." : (
            <>
              {submitLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
        <p className={disclaimerCls}>
          By submitting you agree to receive a call/WhatsApp message from us regarding your enquiry.
        </p>
      </div>
    </form>
  );
}
